import React, { useCallback, useEffect, useState } from "react";
import whatsapp_icon from "../../assets/icons/colorless/whatsapp_icon.svg";
import Modal from "../Modal";
import Api from "../../api";
import { showNotification } from "../../utils";
import { getContactMapUrl } from "./contactMaps";
import "./contactPanel.css";

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const looksLikeEmail = (value) => /@/.test(value);

const looksLikePhone = (value) =>
  /[\d+]/.test(value) && !looksLikeEmail(value);

const phoneDigits = (value) => value.replace(/\D/g, "");

const toWhatsAppUrl = (value) => {
  const digits = phoneDigits(value);
  return digits ? `https://wa.me/${digits}` : null;
};

const splitPhoneSegments = (value) =>
  value
    .split(/\s*\/\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean);

const emptyToNull = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

const contactToForm = (contact) => ({
  name: contact?.name ?? "",
  info1: contact?.info1 ?? "",
  info2: contact?.info2 ?? "",
});

const ContactInfoLine = ({ value }) => {
  const text = String(value).trim();

  if (looksLikeEmail(text)) {
    return (
      <div className="contact-item">
        <a href={`mailto:${text}`} className="contact-item-link">
          {text}
        </a>
      </div>
    );
  }

  if (looksLikePhone(text)) {
    const segments = splitPhoneSegments(text);

    return (
      <div className="contact-item">
        <div className="contact-item-value">
          <img className="whatsapp_icon" src={whatsapp_icon} alt="" />
          <span className="contact-whatsapp-links">
            {segments.map((segment, index) => {
              const waUrl = toWhatsAppUrl(segment);
              return (
                <React.Fragment key={`${segment}-${index}`}>
                  {index > 0 && (
                    <span className="contact-phone-sep"> / </span>
                  )}
                  {waUrl ? (
                    <a
                      href={waUrl}
                      className="contact-whatsapp-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {segment}
                    </a>
                  ) : (
                    <span>{segment}</span>
                  )}
                </React.Fragment>
              );
            })}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-item">
      <span>{text}</span>
    </div>
  );
};

const ConfirmSaveContent = ({ contactName }) => (
  <p>
    ¿Confirmas que deseas guardar los cambios en{" "}
    <strong>{contactName}</strong>? Esta acción actualizará la información
    visible para todos los usuarios.
  </p>
);

const ContactPanel = ({ contact, editMode = false, onContactUpdated }) => {
  const [formData, setFormData] = useState(() => contactToForm(contact));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(contactToForm(contact));
  }, [contact, editMode]);

  const handleFieldChange = useCallback((field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  }, []);

  const handleSaveRequest = () => {
    if (!String(formData.name).trim()) {
      showNotification(
        "¡Aviso!",
        "El nombre del contacto es obligatorio.",
        "warning"
      );
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!contact?.id) return;

    const payload = {
      name: String(formData.name).trim(),
      info1: emptyToNull(formData.info1),
      info2: emptyToNull(formData.info2),
    };

    setSaving(true);
    try {
      const updated = await Api.Contact.Update(contact.id, payload);
      if (!updated?.id) {
        return;
      }
      showNotification(
        "¡Listo!",
        "La información de contacto se actualizó correctamente.",
        "success"
      );
      setShowConfirmModal(false);
      onContactUpdated?.(updated);
    } catch (err) {
      showNotification(
        "Error",
        err.message || "No se pudo guardar la información.",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!contact) return null;

  const mapUrl = getContactMapUrl(contact.name);
  const showInfo1 = hasValue(contact.info1);
  const showInfo2 = hasValue(contact.info2);

  return (
    <div className="contact-panel">
      {editMode ? (
        <>
          <p className="contact-edit-banner">
            Modo edición activo. Los cambios se aplican al guardar.
          </p>
          <form
            className="contact-edit-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleSaveRequest();
            }}
          >
            <label className="contact-edit-field" htmlFor="contact-edit-name">
              <span className="contact-edit-label">Nombre</span>
              <input
                id="contact-edit-name"
                type="text"
                className="contact-edit-input"
                value={formData.name}
                onChange={handleFieldChange("name")}
                disabled={saving}
                required
              />
            </label>
            <label className="contact-edit-field" htmlFor="contact-edit-info1">
              <span className="contact-edit-label">
                Teléfono(s) — info principal
              </span>
              <input
                id="contact-edit-info1"
                type="text"
                className="contact-edit-input"
                value={formData.info1}
                onChange={handleFieldChange("info1")}
                disabled={saving}
                placeholder="Ej: +57 3001234567"
              />
            </label>
            <label className="contact-edit-field" htmlFor="contact-edit-info2">
              <span className="contact-edit-label">
                Correo o info secundaria
              </span>
              <input
                id="contact-edit-info2"
                type="text"
                className="contact-edit-input"
                value={formData.info2}
                onChange={handleFieldChange("info2")}
                disabled={saving}
                placeholder="Ej: contacto@canadiancollege.edu.co"
              />
            </label>
            <button
              type="submit"
              className="contact-save-btn"
              disabled={saving}
            >
              {saving ? "Guardando…" : "Guardar información"}
            </button>
          </form>
        </>
      ) : (
        <>
          <h2 className="contact-panel-title">{contact.name}</h2>
          <div className="contact-panel-body">
            {showInfo1 && <ContactInfoLine value={contact.info1} />}
            {showInfo2 && <ContactInfoLine value={contact.info2} />}
            {!showInfo1 && !showInfo2 && (
              <p className="contact-empty-info">Sin información de contacto.</p>
            )}
          </div>
        </>
      )}

      {mapUrl && (
        <div className="contact-map-wrap">
          <iframe
            src={mapUrl}
            title={`Mapa ${contact.name}`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      )}

      {showConfirmModal && (
        <Modal
          isOpen={showConfirmModal}
          title="Confirmar cambios"
          Content={() => (
            <ConfirmSaveContent contactName={String(formData.name).trim()} />
          )}
          onCancel={() => {
            if (!saving) setShowConfirmModal(false);
          }}
          onSubmit={handleConfirmSave}
        />
      )}
    </div>
  );
};

export default ContactPanel;
